// SearchPatient.tsx
import { useState, useEffect, useRef, useCallback } from "react";
import type { ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "@/contexts/LocationContext";
import {
  Search,
  X,
  MessageSquare,
  ArrowLeft,
  Users,
  Loader2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import ENV from "@/lib/env";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import AuthService from "@/lib/auth";
import { sendBulkSMS } from "@/utils/sendSMS";
import { formatDate, cn, formatPhoneDisplay, formatClinicPhone } from "@/lib/utils";
import MainLayout from "@/layouts/MainLayout";

// Define the Patient type
type ConsentLog = {
  type: string;
  status: "accepted" | "declined" | "pending";
  timestamp: string;
  details: string;
};

type Patient = {
  id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  mobile_number: string | null;
  email: string | null;
  consent_logs: ConsentLog[] | null;
  sms_consent: boolean | null;
  created_at: string;
  updated_at: string | null;
  photo_url: string | null;
  receive_sms: boolean | null;
  auth_uuid: string | null;
  primary_cp_uuid?: string | null;
  secondary_cp_uuid?: string | null;
  primary_provider_id: string | null;
  primary_doctor_id: string | null;
  provider_id: string | null;
  selected_tests?: string[] | null;
  enrolled_for_monitorhealth_services: boolean | null;
  status: string | null;
};

interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

interface SmsEligibilityInfo {
  canSend: boolean;
  canResend: boolean;
  reason: string;
  lastSentTime?: Date;
  sentCount: number;
}

const SearchPatient = () => {
  const navigate = useNavigate();
  const {
    selectedLocationId,
    locations,
  } = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [importing, setImporting] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [allFilteredPatients, setAllFilteredPatients] = useState<Patient[]>([]);
  const [staffProfile, setStaffProfile] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSmsModalOpen, setSmsModalOpen] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [availableTests, setAvailableTests] = useState<any[]>([]);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);
  const [enrolledFilter, setEnrolledFilter] = useState("all");
  const [isClinical, setIsClinical] = useState(false);

  const [smsEligibility, setSmsEligibility] = useState<
    Record<string, SmsEligibilityInfo>
  >({});
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Client-side pagination synchronizer: keeps current page view aligned with allFilteredPatients
  useEffect(() => {
    if (allFilteredPatients.length === 0) {
      setPatients([]);
      return;
    }
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    setPatients(allFilteredPatients.slice(startIndex, endIndex));
  }, [allFilteredPatients, pagination.page, pagination.pageSize]);

  // Add ref to track if SMS is currently being processed
  const smsProcessingRef = useRef(false);

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      // Use the new backend endpoint for CSV upload
      const response = await AuthService.uploadPatientCSV(file);

      if (response.status === "success") {
        toast.success(
          response.message || `Successfully processed ${response.total_rows} patients`
        );
      } else if (response.status === "warning") {
        toast.warning(response.message);
      } else {
        toast.error(response.message || "Failed to import patients");
      }

      await fetchPatients();
    } catch (error: any) {
      console.error("Error importing patients:", error);
      toast.error(error.message || "Failed to import patients");
    } finally {
      setImporting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Memoize fetchPatients to prevent unnecessary re-renders
  const fetchPatients = useCallback(async () => {
    setIsLoading(true);
    try {
      // Check if location is selected
      if (!selectedLocationId) {
        console.log("No location selected");
        setPatients([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        setIsLoading(false);
        return;
      }

      console.log("Fetching patients for location:", selectedLocationId);

      let userId: string | undefined;
      const cognitoUser = await AuthService.getCurrentUser();
      userId = cognitoUser?.username;
      if (!userId) {
        console.error("No authenticated user found");
        setPatients([]);
        setAllFilteredPatients([]);
        return;
      }

      // Get staff user from backend (RDS via API) to check role
      const staffData = await AuthService.getProfile(userId);
      setStaffProfile(staffData);
      const role = staffData?.role?.toLowerCase() || "";

      const clinicalRoles = [
        "doctor", "nurse", "clinical_staff", "clinical staff",
        "nurse practitioner", "physician assistant", "clinical technician"
      ];
      setIsClinical(clinicalRoles.includes(role));

      const isDoctor = role === "doctor";

      let assignedPatientIds: string[] = [];
      if (isDoctor && staffData?.id) {
        const encounters = await AuthService.getPatientEncounters({
          primary_provider_id: staffData.id,
        });
        const secondary = await AuthService.getPatientEncounters({
          secondary_provider_id: staffData.id,
        });
        assignedPatientIds = [...new Set([...encounters, ...secondary].map((e: any) => e.patient_id))];
      }

      // Fetch patients for this location/search with a large batch size to allow complete role filtering
      const res = await AuthService.getPatientsWithFilters({
        location_id: selectedLocationId != null ? String(selectedLocationId) : undefined,
        search: searchTerm || undefined,
        pageSize: 100,
        page: 1,
      });

      let allFetchedPatients: Patient[] = res.data || [];
      if (typeof res.total === 'number' && res.total > allFetchedPatients.length) {
        const totalPages = Math.ceil(res.total / 100);
        const fetches = [];
        for (let p = 2; p <= Math.min(totalPages, 5); p++) {
          fetches.push(
            AuthService.getPatientsWithFilters({
              location_id: selectedLocationId != null ? String(selectedLocationId) : undefined,
              search: searchTerm || undefined,
              pageSize: 100,
              page: p,
            })
          );
        }
        const remaining = await Promise.all(fetches);
        for (const r of remaining) {
          if (r.data) allFetchedPatients = allFetchedPatients.concat(r.data);
        }
      }

      const filteredPatients = allFetchedPatients.filter((patient: Patient) => {
        // Ensure patient has a name
        if (!patient.first_name && !patient.last_name) return false;

        const fullName = `${patient.first_name || ""}${patient.middle_name ? ' ' + patient.middle_name : ''} ${patient.last_name || ""}`.toLowerCase().trim();
        const mobile = (patient.mobile_number || "").replace(/\D/g, "");
        const isDummy = fullName === "registration pending" && (mobile === "0000000000" || mobile === "00000");
        if (isDummy) return false;

        // Apply Role-based filtering: 
        // Admin sees all. Doctor sees:
        // 1. Unenrolled patients (so they can be enrolled)
        // 2. Enrolled patients assigned to this doctor (primary_cp_uuid, secondary_cp_uuid, primary_provider_id, or encounter)
        // 3. Enrolled patients that have NO assigned CP yet (e.g. self-registered or imported)
        // A doctor only hides patients who are enrolled AND assigned to another doctor.
        const isEnrolled = !!patient.enrolled_for_monitorhealth_services;
        const cpUuid = patient.primary_cp_uuid;
        const secCpUuid = patient.secondary_cp_uuid;
        const isAssignedToSelf =
          assignedPatientIds.includes(patient.id) ||
          (!!cpUuid && cpUuid === staffData?.id) ||
          (!!secCpUuid && secCpUuid === staffData?.id) ||
          patient.primary_doctor_id === staffData?.id ||
          patient.primary_provider_id === staffData?.id ||
          patient.provider_id === staffData?.id;

        const isAssignedToOtherDoctor =
          isDoctor &&
          isEnrolled &&
          !isAssignedToSelf &&
          !!cpUuid &&
          cpUuid !== staffData?.id;

        if (isAssignedToOtherDoctor) return false;

        // Apply UI Enrollment Filter
        if (enrolledFilter === "enrolled" && !isEnrolled) return false;
        if (enrolledFilter === "not_enrolled" && isEnrolled) return false;

        return true;
      });

      setAllFilteredPatients(filteredPatients);
      setPagination((prev) => {
        const totalCount = filteredPatients.length;
        const maxPage = Math.max(1, Math.ceil(totalCount / prev.pageSize));
        const newPage = prev.page > maxPage ? maxPage : prev.page;
        return {
          ...prev,
          page: newPage,
          total: totalCount,
        };
      });
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedLocationId,
    searchTerm,
    enrolledFilter,
  ]);

  const fetchTests = useCallback(async () => {
    try {
      const templates = await AuthService.getQuestionnaireTemplates({ is_active: true });
      setAvailableTests(templates || []);
    } catch (error) {
      console.error("Error fetching tests:", error);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  // Helper function to get the latest consent status from logs
  const getLatestConsentStatus = (
    logs: ConsentLog[] | null,
    type?: string,
  ): string => {
    if (!logs || logs.length === 0) return "pending";

    // Filter logs by type if specified
    const filteredLogs = type ? logs.filter((log) => log.type === type) : logs;

    if (filteredLogs.length === 0) return "pending";

    // Sort logs by timestamp in descending order to get the most recent first
    const sortedLogs = [...filteredLogs].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );

    return sortedLogs[0].status || "pending";
  };

  // Helper function to check SMS eligibility for each patient
  const checkSMSEligibility = async (
    patientId: string,
  ): Promise<{
    canSend: boolean;
    canResend: boolean;
    reason: string;
    lastSentTime?: Date;
    sentCount: number;
  }> => {
    try {
      // 1. Verify if the patient has assigned quality forms
      const patientPool = allFilteredPatients.length > 0 ? allFilteredPatients : patients;
      let targetPatient = patientPool.find((p) => p.id === patientId);
      if (!targetPatient) {
        targetPatient = await AuthService.getPatient(patientId);
      }

      const hasQualityForms = Boolean(
        (targetPatient?.selected_tests && targetPatient.selected_tests.length > 0) ||
        (targetPatient as any)?.has_selected_tests
      );

      if (!hasQualityForms) {
        return {
          canSend: false,
          canResend: false,
          reason: "No Quality Forms Assigned",
          sentCount: 0,
        };
      }

      // 2. Time-based SMS count logic (within rolling 24-hour window)
      const smsLogs = await AuthService.getSMSLogsByPatient(patientId);
      const now = Date.now();
      const oneDayAgo = now - 24 * 60 * 60 * 1000;

      const sortedLogs = (smsLogs || []).slice().sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at).getTime() -
          new Date(a.updated_at || a.created_at).getTime(),
      );

      // Count SMS messages sent in the last 24 hours
      const recentLogs = sortedLogs.filter((log) => {
        const t = new Date(log.updated_at || log.created_at).getTime();
        return !isNaN(t) && t >= oneDayAgo;
      });

      const sentCount = recentLogs.length;
      const lastSentTime = sortedLogs.length > 0
        ? new Date(sortedLogs[0].updated_at || sortedLogs[0].created_at)
        : undefined;

      // Check if reached limit (3 messages in 24 hours)
      if (sentCount >= 3) {
        return {
          canSend: false,
          canResend: false,
          reason: "Maximum SMS limit reached (3 in 24h)",
          lastSentTime,
          sentCount,
        };
      }

      // If already sent in current 24-hour window (< 3), allow resend
      if (sentCount > 0) {
        return {
          canSend: false,
          canResend: true,
          reason: "Ready to resend",
          lastSentTime,
          sentCount,
        };
      }

      // No SMS sent in last 24 hours -> Ready to send
      return {
        canSend: true,
        canResend: false,
        reason: "Ready to send",
        lastSentTime,
        sentCount: 0,
      };
    } catch (error) {
      console.error("Exception checking SMS eligibility:", error);
      return {
        canSend: false,
        canResend: false,
        reason: "Error checking SMS status",
        sentCount: 0,
      };
    }
  };

  // Helper function to check if SMS consent is given
  const hasSMSConsent = (logs: ConsentLog[] | null): boolean => {
    return getLatestConsentStatus(logs, "sms_consent") === "accepted";
  };

  // Function to check SMS eligibility for all selected patients
  const checkAllSMSEligibility = async () => {
    if (selectedPatients.length === 0) return;

    const eligibilityResults: Record<string, any> = {};

    await Promise.all(
      selectedPatients.map(async (patientId) => {
        const eligibility = await checkSMSEligibility(patientId);
        eligibilityResults[patientId] = eligibility;
      }),
    );

    setSmsEligibility(eligibilityResults);
  };

  // Check eligibility when SMS modal opens or selected patients change
  useEffect(() => {
    if (isSmsModalOpen && selectedPatients.length > 0) {
      checkAllSMSEligibility();
    }
  }, [isSmsModalOpen, selectedPatients]);

  // Refresh patients when location changes
  useEffect(() => {
    if (selectedLocationId) {
      console.log("Location changed to:", selectedLocationId);
      // Reset pagination and search when location changes
      setPagination((prev) => ({ ...prev, page: 1 }));
      setSearchTerm("");
      setSelectedPatients([]);
    }
  }, [selectedLocationId]);

  // Debounce search term and pagination changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchPatients();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchPatients]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPatients((prev) => {
        const currentIds = patients.map((p) => p.id);
        const combined = new Set([...prev, ...currentIds]);
        return Array.from(combined);
      });
    } else {
      const currentIds = new Set(patients.map((p) => p.id));
      setSelectedPatients((prev) => prev.filter((id) => !currentIds.has(id)));
    }
  };

  const handleSelectPatient = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedPatients((prev) => [...prev, patientId]);
    } else {
      setSelectedPatients((prev) => prev.filter((id) => id !== patientId));
    }
  };

  const handleSendSms = async (patientIds?: string[]) => {
    // Use provided patient IDs or fall back to selected patients
    const targetPatients = patientIds || selectedPatients;

    // Prevent multiple simultaneous SMS sending
    if (smsProcessingRef.current || isSending) {
      console.warn(
        "SMS sending already in progress, ignoring duplicate request",
      );
      return;
    }

    smsProcessingRef.current = true;
    setIsSending(true);

    try {
      // Get current doctor's profile to personalize the message
      const cognitoUser = await AuthService.getCurrentUser();
      const currentStaffProfile = cognitoUser?.username ? await AuthService.getProfile(cognitoUser.username) : null;

      // Filter from existing local state (full pool) rather than single page slice
      const patientPool = allFilteredPatients.length > 0 ? allFilteredPatients : patients;
      let targetPatientDetails = patientPool.filter(p => targetPatients.includes(p.id));

      if (targetPatientDetails.length === 0) {
        toast.error("No patients selected");
        return;
      }

      // Prepare messages
      const messages = [];

      for (const patient of targetPatientDetails) {
        if (!patient.mobile_number) {
          toast.error(`Patient ${patient.first_name || "Unknown"} does not have a mobile number`);
          continue;
        }

        // Check eligibility for sending (we still check against the patient's ID for logging and frequency limits)
        const eligibility = await checkSMSEligibility(patient.id);
        if (!eligibility.canSend) {
          toast.error(
            `Patient ${patient.first_name || "Unknown"} cannot be sent more SMS: ${eligibility.reason}`,
          );
          continue;
        }

        const uniqueUrl = await generateSMSEmbedUrlForPatient(patient.id);

        if (uniqueUrl) {
          const firstName = patient.first_name?.trim() || "there";
          const patientFullName = `${patient.first_name || ""}${patient.middle_name ? ' ' + patient.middle_name : ''} ${patient.last_name || ""}`.trim();

          const selectedLoc = locations.find((l) => l.value === selectedLocationId);
          const rawPhone = selectedLoc?.phone || "(555) 000-0004";
          const clinicPhone = formatClinicPhone(rawPhone);
          const hName = selectedLoc?.hospitalName || "Ally Psychiatry";
          const rawBranch = selectedLoc?.branchName || "Pelham";
          const bName = rawBranch.replace(/\s*branch$/i, "").trim() || "Pelham";
          const clinicHeader = bName && !hName.toLowerCase().includes(bName.toLowerCase())
            ? `${hName} (${bName})`
            : hName;

          // Personalized message to the patient matching template:
          // “{clinicHeader} via MonitorHealth: Hi {first_name}, your care team has sent you a health questionnaire to complete: {link}
          // This link expires in 48 hours. Questions? Call {clinic_phone}. Text STOP to opt out.”
          const messageBody = `${clinicHeader} via MonitorHealth: Hi ${firstName}, your care team has sent you a health questionnaire to complete: ${uniqueUrl}\nThis link expires in 48 hours. Questions? Call ${clinicPhone}. Text STOP to opt out.`;

          messages.push({
            to: patient.mobile_number,
            body: messageBody,
            patientId: patient.id,
            uniqueUrl: uniqueUrl,
            // Custom name for toast identification: "Sending SMS to [Patient]..."
            name: `${patientFullName}`
          });
        }
      }

      if (messages.length > 0) {
        // Send messages using the SignalWire API
        const { success, count } = await sendBulkSMS(
          messages.map((msg) => ({
            to: msg.to,
            body: msg.body,
            name: msg.name
          })),
        );

        if (success) {
          const smsLogEntries = messages.map((msg) => ({
            patient_id: msg.patientId,
            sent_times: 1,
            creator_id: currentStaffProfile?.id || null, // The person who initiated the send
            unique_page_id: msg.uniqueUrl.split("/").pop(),
            message_body: msg.body,
            status: "sent",
          }));
          await AuthService.createSMSLog(smsLogEntries);

          toast.success(`Successfully sent ${count} SMS messages to providers`);
          setSmsModalOpen(false);
          setSelectedPatients([]);
        } else {
          toast.error(
            `Sent ${count} of ${messages.length} messages. Some messages failed to send.`,
          );
        }
      } else {
        // If no messages were prepared but we had target patients, errors would have been shown by toasts above
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast.error("Failed to send SMS. Please try again.");
    } finally {
      setIsSending(false);
      smsProcessingRef.current = false;
    }
  };

  const handleResendSms = async (patientIds?: string[]) => {
    // Use provided patient IDs or fall back to selected patients
    const targetPatients = patientIds || selectedPatients;

    // Prevent multiple simultaneous SMS sending
    if (smsProcessingRef.current || isSending) {
      console.warn(
        "SMS sending already in progress, ignoring duplicate request",
      );
      return;
    }

    smsProcessingRef.current = true;
    setIsSending(true);

    try {
      const currentStaffProfile = await AuthService.getProfile();

      // Filter from existing local state (full pool) rather than single page slice
      const patientPool = allFilteredPatients.length > 0 ? allFilteredPatients : patients;
      let targetPatientDetails = patientPool.filter(p => targetPatients.includes(p.id));

      if (targetPatientDetails.length === 0) {
        toast.error("No patients selected");
        return;
      }

      // Prepare messages
      const messages = [];

      for (const patient of targetPatientDetails) {
        if (!patient.mobile_number) {
          toast.error(`Patient ${patient.first_name || "Unknown"} does not have a mobile number`);
          continue;
        }

        // Check eligibility for resend
        const eligibility = await checkSMSEligibility(patient.id);
        if (!eligibility.canResend) {
          toast.error(
            `Patient ${patient.first_name}${patient.middle_name ? ' ' + patient.middle_name : ''} ${patient.last_name} cannot resend SMS: ${eligibility.reason}`,
          );
          continue;
        }

        const uniqueUrl = await generateSMSEmbedUrlForPatient(patient.id);

        if (uniqueUrl) {
          const firstName = patient.first_name?.trim() || "there";
          const patientFullName = `${patient.first_name || ""}${patient.middle_name ? ' ' + patient.middle_name : ''} ${patient.last_name || ""}`.trim();

          const selectedLoc = locations.find((l) => l.value === selectedLocationId);
          const rawPhone = selectedLoc?.phone || "(555) 000-0004";
          const clinicPhone = formatClinicPhone(rawPhone);
          const hName = selectedLoc?.hospitalName || "Ally Psychiatry";
          const rawBranch = selectedLoc?.branchName || "Pelham";
          const bName = rawBranch.replace(/\s*branch$/i, "").trim() || "Pelham";
          const clinicHeader = bName && !hName.toLowerCase().includes(bName.toLowerCase())
            ? `${hName} (${bName})`
            : hName;

          const message = `${clinicHeader} via MonitorHealth: Hi ${firstName}, your care team has sent you a health questionnaire to complete: ${uniqueUrl}\nThis link expires in 48 hours. Questions? Call ${clinicPhone}. Text STOP to opt out.`;

          messages.push({
            to: patient.mobile_number,
            body: message,
            patientId: patient.id,
            uniqueUrl: uniqueUrl,
            name: `${patientFullName}`
          });
        }
      }

      if (messages.length > 0) {
        // Send messages using the SignalWire API
        const { success, count } = await sendBulkSMS(
          messages.map((msg) => ({
            to: msg.to,
            body: msg.body,
            name: msg.name
          })),
        );

        if (success) {
          // Log each resent message in the database
          const smsLogEntries = messages.map((msg) => ({
            patient_id: msg.patientId,
            sent_times: 1,
            creator_id: currentStaffProfile?.id || null,
            unique_page_id: msg.uniqueUrl.split("/").pop(),
            message_body: msg.body,
            status: "sent",
          }));

          await AuthService.createSMSLog(smsLogEntries);
          toast.success(`Successfully resent ${count} SMS messages to providers`);
          setSmsModalOpen(false);
          setSelectedPatients([]);
        } else {
          toast.error(
            `Resent ${count} of ${messages.length} messages. Some messages failed to send.`,
          );
        }
      } else {
        // Errors shown by toasts above
      }
    } catch (error) {
      console.error("Error resending SMS:", error);
      toast.error("Failed to resend SMS. Please try again.");
    } finally {
      setIsSending(false);
      smsProcessingRef.current = false;
    }
  };

  const handleBulkAssign = async () => {
    if (selectedPatients.length === 0 || selectedTests.length === 0) return;

    setUpdating(true);
    try {
      const isDoctorUser = staffProfile?.role?.toLowerCase() === "doctor";
      const batchSize = 10;
      for (let i = 0; i < selectedPatients.length; i += batchSize) {
        const batch = selectedPatients.slice(i, i + batchSize);
        await Promise.all(batch.map(id =>
          AuthService.updatePatient(id, {
            selected_tests: selectedTests,
            enrolled_for_monitorhealth_services: true,
            ...(isDoctorUser && staffProfile?.id ? {
              primary_cp_uuid: staffProfile.id,
              primary_cp_name: staffProfile.name || (staffProfile as any).full_name || '',
              primary_cp_email: staffProfile.email || '',
            } : {})
          })
        ));
      }

      toast.success(`Successfully assigned tests to ${selectedPatients.length} patients`);
      setIsBulkAssignOpen(false);
      setSelectedTests([]);
      setSelectedPatients([]);
      await fetchPatients();
    } catch (error) {
      console.error("Error in bulk assign:", error);
      toast.error("Failed to assign tests to some patients");
    } finally {
      setUpdating(false);
    }
  };

  const toggleTest = (testId: string) => {
    setSelectedTests(prev =>
      prev.includes(testId)
        ? prev.filter(id => id !== testId)
        : (prev.length < 2 ? [...prev, testId] : prev)
    );
  };

  const generateSMSEmbedUrlForPatient = async (
    patientId: string,
  ): Promise<string> => {
    const url = ENV.VITE_BASE_URL || window.location.origin;
    //const maxRetries = 5;
    const maxRetries = 10;
    let attempts = 0;

    const smsLogs = await AuthService.getSMSLogsByPatient(patientId);
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const recentLogs = (smsLogs || []).filter((log) => {
      const t = new Date(log.updated_at || log.created_at).getTime();
      return !isNaN(t) && t >= oneDayAgo;
    });

    if (recentLogs.length >= 3) {
      console.error(
        `[SMS] Patient ${patientId} has already received ${recentLogs.length} SMS messages in the last 24 hours`,
      );
      return "";
    }

    while (attempts < maxRetries) {
      // Generate random 6-8 character alphanumeric string
      const charset = "23456789abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ";
      const array = new Uint8Array(8);
      crypto.getRandomValues(array);
      const randomString = Array.from(array).map((b) => charset[b % charset.length]).join("");

      if (
        !randomStringIsBad(randomString) &&
        !(await AuthService.checkSMSUniquePageId(randomString))
      ) {
        return `${url}/q/${randomString}`;
      }

      attempts++;
      if (attempts >= maxRetries) {
        console.error(
          `[SMS] Max retries reached generating unique URL for patient ${patientId}`,
        );
        return "";
      }
    }

    return "";
  };

  const calculateAge = (dob: string | null): number | null => {
    if (!dob) return null;
    const dobDate = new Date(dob);
    if (isNaN(dobDate.getTime())) return null;
    const diff = Date.now() - dobDate.getTime();
    return diff < 0 ? 0 : Math.abs(new Date(diff).getUTCFullYear() - 1970);
  };

  const validBulkTests = availableTests.filter((test) => {
    const patientPool = allFilteredPatients.length > 0 ? allFilteredPatients : patients;
    const selectedPatientData = patientPool.filter((p) => selectedPatients.includes(p.id));
    if (selectedPatientData.length === 0) return true;

    // A test is valid ONLY if it fits the age range of EVERY selected patient
    return selectedPatientData.every((patient) => {
      const patientAge = calculateAge(patient.date_of_birth);
      if (patientAge === null) return true; // If no DOB, assume eligible or follow existing logic

      const min = typeof test.min_age === "number" && !isNaN(test.min_age) ? test.min_age :
        (typeof test.age_min === "number" && !isNaN(test.age_min) ? test.age_min : 0);
      const max = typeof test.max_age === "number" && !isNaN(test.max_age) ? test.max_age :
        (typeof test.age_max === "number" && !isNaN(test.age_max) ? test.age_max : 999);

      return patientAge >= min && patientAge <= max;
    });
  });

  const randomStringIsBad = (str: string): boolean => {
    const badWords = ["fkr", "mthfkr", "ngr", "nigger", "fck", "sht"];
    return badWords.some((word) => str.toLowerCase().includes(word));
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 pb-36 lg:pb-44 font-sans">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-12 w-12 rounded-2xl border-gray-100 bg-white shadow-sm hover:shadow-md transition-all"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div>
                <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                  Patients
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-500 font-medium">
                    Manage and search for patients in the system
                  </p>
                  {selectedLocationId && (
                    <Badge variant="outline" className="text-[10px] h-5 bg-white border-gray-100 text-[#1a7a4a] uppercase tracking-wider font-bold px-2">
                      {locations.find((l) => l.value === selectedLocationId)?.label || "Unknown Location"}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing || !selectedLocationId}
                className="h-11 px-6 rounded-2xl border-gray-200 text-gray-600 hover:text-[#1a7a4a] hover:bg-gray-50 transition-all font-semibold flex items-center gap-2"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Import CSV
              </Button>
              {isClinical && (
                <Button
                  variant="outline"
                  onClick={() => setIsBulkAssignOpen(true)}
                  disabled={selectedPatients.length === 0 || !selectedLocationId}
                  className="h-11 px-6 rounded-2xl border-emerald-100 text-emerald-700 hover:bg-emerald-50 transition-all font-semibold flex items-center gap-2"
                >
                  Bulk Assign Tests
                </Button>
              )}

              <Button
                onClick={() => setSmsModalOpen(true)}
                disabled={selectedPatients.length === 0 || isSending}
                className="h-11 px-6 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
              >
                <MessageSquare className="h-4 w-4" />
                Send SMS ({selectedPatients.length})
              </Button>
            </div>
          </div>

          {/* No Location Selected Warning */}
          {!selectedLocationId && (
            <div className="bg-amber-50 border border-amber-100 rounded-3xl p-6 flex items-center gap-4 text-amber-800 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <Search className="h-6 w-6 text-amber-500" />
              </div>
              <span className="text-lg font-bold">
                Please select a Hospital and Branch from the header to view patients.
              </span>
            </div>
          )}

          {/* Search and Filters */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-visible">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="relative flex-grow group">
                  <input
                    type="text"
                    placeholder="Search by name, phone, or email..."
                    className="pl-12 pr-4 h-12 w-full bg-gray-50/50 border-transparent focus:bg-white focus:border-[#1a7a4a] focus:ring-[#1a7a4a] rounded-2xl text-sm transition-all shadow-sm group-hover:shadow-md"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTerm(e.target.value)
                    }
                    onKeyPress={(e: React.KeyboardEvent) =>
                      e.key === "Enter" && fetchPatients()
                    }
                    disabled={!selectedLocationId}
                  />
                  <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 group-hover:text-[#1a7a4a] transition-colors" />
                </div>

                <div className="flex gap-4">
                  <Select value={enrolledFilter} onValueChange={setEnrolledFilter}>
                    <SelectTrigger className="w-full lg:w-[200px] h-12 rounded-2xl border-gray-100 bg-white hover:bg-gray-50 transition-all font-semibold">
                      <SelectValue placeholder="Enrolled Status" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-gray-100 shadow-xl">
                      <SelectItem value="all">All Patients</SelectItem>
                      <SelectItem value="enrolled">Enrolled</SelectItem>
                      <SelectItem value="not_enrolled">Not Enrolled</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={fetchPatients}
                    disabled={isLoading || !selectedLocationId}
                    className="h-12 px-8 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold transition-all shadow-lg shadow-green-100 hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching...
                      </div>
                    ) : "Search"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-4">
            {!selectedLocationId ? (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 p-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-gray-200" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No location selected</h3>
                <p className="text-gray-500 text-lg">Please select a Hospital and Branch from the header to view patients.</p>
              </div>
            ) : isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a7a4a]"></div>
              </div>
            ) : patients.length > 0 ? (
              <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="px-6 py-5 w-12 text-center">
                          <input
                            type="checkbox"
                            checked={patients.length > 0 && patients.every((p) => selectedPatients.includes(p.id))}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="h-5 w-5 rounded-lg border-gray-300 text-[#1a7a4a] focus:ring-[#1a7a4a] transition-all cursor-pointer"
                          />
                        </th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Name</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Gender/Age</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">Enrolled</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">HIPAA Consent</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">SMS Consent</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500 text-right pr-10">Onboarded On</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {patients.map((patient) => (
                        <tr key={patient.id} className={`group transition-colors ${patient.status === 'incomplete' ? 'bg-orange-50/60 border-l-4 border-l-orange-400 hover:bg-orange-50' : 'hover:bg-[#1a7a4a]/5'}`}>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedPatients.includes(patient.id)}
                              onChange={(e) =>
                                handleSelectPatient(patient.id, e.target.checked)
                              }
                              className="h-5 w-5 rounded-lg border-gray-300 text-[#1a7a4a] focus:ring-[#1a7a4a] transition-all cursor-pointer"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold">
                                {patient.first_name?.[0]}{patient.last_name?.[0]}
                              </div>
                              <div
                                className="font-bold text-gray-900 cursor-pointer hover:text-[#1a7a4a] transition-colors leading-tight"
                                onClick={() => navigate(`/patients/${patient.id}`)}
                              >
                                <div className="flex items-center gap-2">
                                  {patient.first_name}{patient.middle_name ? ` ${patient.middle_name}` : ''} {patient.last_name}
                                  {patient.status === 'incomplete' && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-600 border border-orange-200 uppercase tracking-wide">
                                      Incomplete
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-mono text-gray-400 mt-0.5">{formatPhoneDisplay(patient.mobile_number) || "No number"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-600">
                            {patient.gender || "?"} / {calculateAge(patient.date_of_birth) ?? "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className={cn(
                              "inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider",
                              patient.enrolled_for_monitorhealth_services
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-gray-50 text-gray-400 border-gray-100"
                            )}>
                              {patient.enrolled_for_monitorhealth_services ? "Enrolled" : "Not Enrolled"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={cn(
                              "inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider",
                              getLatestConsentStatus(patient.consent_logs, "hipaa_consent") === "accepted"
                                ? "bg-blue-50 text-blue-700 border-blue-100"
                                : "bg-gray-50 text-gray-400 border-gray-100"
                            )}>
                              {getLatestConsentStatus(patient.consent_logs, "hipaa_consent") === "accepted" ? "Accepted" : "Pending"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={cn(
                              "inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider",
                              hasSMSConsent(patient.consent_logs)
                                ? "bg-purple-50 text-purple-700 border-purple-100"
                                : "bg-gray-50 text-gray-400 border-gray-100"
                            )}>
                              {hasSMSConsent(patient.consent_logs) ? "Accepted" : "Pending"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm font-medium text-right pr-10">
                            {formatDate(patient.created_at, "MMM d, yyyy")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 p-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-gray-200" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No patients found</h3>
                <p className="text-gray-500 text-lg">Try adjusting your search terms or filters to find patients.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {selectedLocationId && pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 mt-6 sm:pr-48">
              <div className="text-sm text-gray-500 font-medium bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm">
                Showing{" "}
                <span className="text-[#1a7a4a] font-bold">
                  {patients.length === 0
                    ? 0
                    : (pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="text-[#1a7a4a] font-bold">
                  {patients.length === 0
                    ? 0
                    : (pagination.page - 1) * pagination.pageSize + patients.length}
                </span>{" "}
                of <span className="text-gray-900 font-bold">{pagination.total}</span>{" "}
                patients
              </div>

              <div className="flex items-center gap-3">
                <div className="flex bg-white rounded-2xl border border-gray-100 shadow-sm p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: Math.max(1, prev.page - 1),
                      }))
                    }
                    disabled={pagination.page === 1 || isLoading}
                    className="h-9 px-4 rounded-xl text-xs font-bold hover:bg-gray-50"
                  >
                    Previous
                  </Button>
                  <div className="w-px h-4 bg-gray-100 self-center mx-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={
                      pagination.page * pagination.pageSize >= pagination.total ||
                      isLoading
                    }
                    className="h-9 px-4 rounded-xl text-xs font-bold hover:bg-gray-50"
                  >
                    Next
                  </Button>
                </div>

                <select
                  className="h-11 rounded-2xl border border-gray-100 bg-white px-4 py-1 text-sm font-bold text-gray-600 shadow-sm focus:ring-2 focus:ring-[#1a7a4a]/20 outline-none transition-all"
                  value={pagination.pageSize}
                  onChange={(e) =>
                    setPagination((prev) => ({
                      ...prev,
                      pageSize: Number(e.target.value),
                      page: 1,
                    }))
                  }
                  disabled={isLoading}
                >
                  <option value="5">5 / page</option>
                  <option value="10">10 / page</option>
                  <option value="20">20 / page</option>
                  <option value="50">50 / page</option>
                </select>
              </div>
            </div>
          )}

          {/* SMS Modal */}
          <Dialog open={isSmsModalOpen} onOpenChange={setSmsModalOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <MessageSquare className="text-[#1a7a4a]" />
                  Patient SMS Queue
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-4">
                <div className="bg-[#1a7a4a]/5 p-6 rounded-3xl border border-[#1a7a4a]/10 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#1a7a4a] animate-pulse" />
                      <span className="text-sm font-bold text-gray-700">
                        {selectedPatients.length} {selectedPatients.length === 1 ? 'Patient' : 'Patients'} Selected
                      </span>
                    </div>
                    <Badge variant="outline" className="bg-white border-[#1a7a4a]/20 text-[#1a7a4a] font-bold">
                      SMS Status
                    </Badge>
                  </div>

                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {(allFilteredPatients.length > 0 ? allFilteredPatients : patients)
                      .filter(
                        (patient) =>
                          patient.mobile_number &&
                          selectedPatients.includes(patient.id),
                      )
                      .map((patient) => {
                        const eligibility = smsEligibility[patient.id];
                        const canSend = eligibility?.canSend || false;
                        const canResend = eligibility?.canResend || false;
                        const sentCount = eligibility?.sentCount || 0;
                        const lastSentTime = eligibility?.lastSentTime;

                        return (
                          <div
                            key={patient.id}
                            className="flex justify-between items-start p-3.5 rounded-2xl bg-white border border-gray-100 shadow-sm transition-all hover:shadow-md"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold text-xs shrink-0 mt-0.5">
                                {patient.first_name?.[0] || 'P'}{patient.last_name?.[0] || ''}
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-sm text-gray-900 leading-snug">
                                  {patient.first_name}{patient.middle_name ? ` ${patient.middle_name}` : ''} {patient.last_name}
                                </span>
                                <span className="text-xs text-gray-500 font-medium mt-0.5">
                                  {formatPhoneDisplay(patient.mobile_number) || "No phone number"}
                                </span>
                                {eligibility && (
                                  <div className="flex flex-col gap-0.5 mt-2 text-[11px] text-gray-500 bg-gray-50/55 p-2 rounded-xl border border-gray-100/50">
                                    <div className="flex justify-between">
                                      <span className="font-medium text-gray-400">Total SMS Sent:</span>
                                      <span className="font-bold text-gray-700">{sentCount}/3</span>
                                    </div>
                                    {lastSentTime && (
                                      <div className="flex justify-between">
                                        <span className="font-medium text-gray-400">Last Sent:</span>
                                        <span className="font-bold text-gray-700">{formatDate(lastSentTime, "MMM d, yyyy h:mm a")}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-100/30">
                                      <span className="font-medium text-gray-400">Status:</span>
                                      <span
                                        className={cn(
                                          "font-bold uppercase tracking-tight text-[9px] px-1.5 py-0.5 rounded",
                                          canSend
                                            ? "bg-emerald-50 text-emerald-700"
                                            : canResend
                                              ? "bg-amber-50 text-amber-700"
                                              : "bg-rose-50 text-rose-700"
                                        )}
                                      >
                                        {eligibility.reason}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {(canSend || canResend) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleSelectPatient(patient.id, false)
                                  }
                                  disabled={isSending}
                                  className="h-7 w-7 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 shrink-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 rounded-2xl border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all"
                    onClick={() => setSmsModalOpen(false)}
                    disabled={isSending}
                  >
                    Cancel
                  </Button>

                  {(() => {
                    const eligiblePatients = selectedPatients.filter(
                      (id) => smsEligibility[id]?.canSend,
                    );
                    const resendPatients = selectedPatients.filter(
                      (id) => smsEligibility[id]?.canResend,
                    );

                    if (eligiblePatients.length > 0 && resendPatients.length > 0) {
                      return (
                        <div className="flex flex-1 gap-2">
                          <Button
                            onClick={() => handleSendSms(eligiblePatients)}
                            disabled={isSending}
                            className="flex-1 h-12 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold shadow-lg shadow-green-100 transition-all hover:scale-[1.02] disabled:opacity-50"
                          >
                            {isSending ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending...
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                Send ({eligiblePatients.length})
                              </div>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleResendSms(resendPatients)}
                            disabled={isSending}
                            variant="outline"
                            className="flex-1 h-12 rounded-2xl border-[#1a7a4a]/20 text-[#1a7a4a] hover:bg-[#1a7a4a]/5 font-bold transition-all hover:scale-[1.02]"
                          >
                            {isSending ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Resending...
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                Resend ({resendPatients.length})
                              </div>
                            )}
                          </Button>
                        </div>
                      );
                    } else if (eligiblePatients.length > 0) {
                      return (
                        <Button
                          onClick={() => handleSendSms(eligiblePatients)}
                          disabled={isSending || eligiblePatients.length === 0}
                          className="flex-grow h-12 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold shadow-lg shadow-green-100 transition-all hover:scale-[1.02] disabled:opacity-50"
                        >
                          {isSending ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending...
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Send SMS ({eligiblePatients.length})
                            </div>
                          )}
                        </Button>
                      );
                    } else if (resendPatients.length > 0) {
                      return (
                        <Button
                          onClick={() => handleResendSms(resendPatients)}
                          disabled={isSending || resendPatients.length === 0}
                          className="flex-grow h-12 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold shadow-lg shadow-green-100 transition-all hover:scale-[1.02] disabled:opacity-50"
                        >
                          {isSending ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Resending...
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Resend SMS ({resendPatients.length})
                            </div>
                          )}
                        </Button>
                      );
                    } else {
                      return (
                        <Button disabled className="flex-grow h-12 rounded-2xl bg-gray-200 text-gray-400 font-bold cursor-not-allowed">
                          SMS Not Available
                        </Button>
                      );
                    }
                  })()}
                </div>
              </div>
            </DialogContent>
          </Dialog>          {/* Bulk Assign Tests Dialog */}
          <Dialog open={isBulkAssignOpen} onOpenChange={setIsBulkAssignOpen}>
            <DialogContent className="sm:max-w-[400px] rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
              <DialogHeader className="p-6 bg-white border-b border-gray-50">
                <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight">Bulk Assign Tests</DialogTitle>
                <DialogDescription className="text-gray-400 text-sm mt-1">
                  Assign clinical tests to {selectedPatients.length} selected patients.
                </DialogDescription>
              </DialogHeader>

              <div className="p-6 space-y-4 bg-gray-50/30">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Select up to 2 tests</div>
                <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                  {validBulkTests.length > 0 ? (
                    validBulkTests.map((test) => (
                      <div
                        key={test.id}
                        className={cn(
                          "flex items-center p-3 rounded-xl border cursor-pointer transition-all duration-200",
                          selectedTests.includes(test.id)
                            ? "bg-emerald-50 border-emerald-200 shadow-sm"
                            : "bg-white border-gray-100 hover:border-emerald-100 hover:bg-gray-50",
                          selectedTests.length >= 2 && !selectedTests.includes(test.id) && "opacity-40 cursor-not-allowed grayscale-[0.5]"
                        )}
                        onClick={() => toggleTest(test.id)}
                      >
                        <div className={cn(
                          "h-5 w-5 rounded-md border flex items-center justify-center transition-all",
                          selectedTests.includes(test.id)
                            ? "bg-[#1a7a4a] border-[#1a7a4a]"
                            : "bg-white border-gray-200"
                        )}>
                          {selectedTests.includes(test.id) && (
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="ml-3 flex-1 flex flex-col">
                          <Label className="text-sm font-semibold text-gray-700 cursor-pointer">
                            {test.full_name || test.name || test.id}
                          </Label>
                          {test.source_citations && (
                            <span className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 italic">
                              Citation: {typeof test.source_citations === 'string' ? test.source_citations : JSON.stringify(test.source_citations)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-dashed border-gray-200">
                      <p className="text-sm">No overlapping tests found for the selected patients' age range.</p>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="p-4 bg-white border-t border-gray-50 flex flex-row gap-3 sm:justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setIsBulkAssignOpen(false)}
                  disabled={updating}
                  className="rounded-xl text-gray-500 hover:bg-gray-100 font-semibold flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkAssign}
                  disabled={selectedTests.length === 0 || updating}
                  className="flex-1 sm:flex-none rounded-xl bg-[#1a7a4a] text-white font-bold hover:bg-[#155e39] transition-all shadow-md shadow-[#1a7a4a]/20"
                >
                  {updating ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </div>
                  ) : "Assign & Continue"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </MainLayout>
  );
};

export default SearchPatient;
